import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Mail, Phone, MessageCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { SITE_CONTACT } from '../constants/siteContact';

const NAVBAR_HEIGHT = 72;

const Navbar = () => {
  const { t } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { scrollToTop } = useAppNavigation();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    handleResize();
    handleScroll();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const scrollY = window.scrollY;
    const { body, documentElement } = document;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    documentElement.style.overflow = 'hidden';

    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.overflow = '';
      body.style.touchAction = '';
      documentElement.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/services', label: t('nav.services') },
    { path: '/portfolio', label: t('nav.portfolio') },
    { path: '/team', label: t('nav.team') },
    { path: '/blog', label: t('nav.blog') },
    { path: '/careers', label: t('nav.careers') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') }
  ];

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
    scrollToTop();
  };

  return (
    <>
      <nav style={{
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        background: isScrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(12px)',
        boxShadow: isScrolled ? '0 8px 30px rgba(0, 0, 0, 0.12)' : '0 2px 16px rgba(0, 0, 0, 0.06)',
        zIndex: 1000,
        padding: '0 20px',
        borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
        transition: 'background 0.3s ease, box-shadow 0.3s ease'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 0'
        }}>
          
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/favicon.svg"
              alt="DevAlpha"
              width={36}
              height={36}
              style={{ borderRadius: '10px', display: 'block' }}
            />
            <span style={{
              fontSize: '1.8rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #1e293b, #3b82f6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.5px'
            }}>
              DevAlpha
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div style={{
            display: isMobile ? 'none' : 'flex',
            alignItems: 'center',
            gap: '15px'
          }}>
            {/* Navigation Links */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(248, 250, 252, 0.8)',
              padding: '6px 12px',
              borderRadius: '16px',
              border: '1px solid rgba(226, 232, 240, 0.6)'
            }}>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => handleNavClick()}
                  style={{
                    textDecoration: 'none',
                    color: location.pathname === link.path ? '#3b82f6' : '#64748b',
                    fontWeight: location.pathname === link.path ? '600' : '500',
                    fontSize: '0.85rem',
                    transition: 'all 0.3s ease',
                    padding: '8px 14px',
                    borderRadius: '10px',
                    background: location.pathname === link.path 
                      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))' 
                      : 'transparent',
                    border: location.pathname === link.path 
                      ? '1px solid rgba(59, 130, 246, 0.2)' 
                      : '1px solid transparent',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => {
                    if (location.pathname !== link.path) {
                      e.currentTarget.style.color = '#1e293b';
                      e.currentTarget.style.background = 'rgba(241, 245, 249, 0.8)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (location.pathname !== link.path) {
                      e.currentTarget.style.color = '#64748b';
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {link.label}
                  {location.pathname === link.path && (
                    <div style={{
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      position: 'absolute',
                      bottom: '-8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#3b82f6'
                    }} />
                  )}
                </Link>
              ))}
            </div>
            
            {/* Language Selector */}
            <LanguageSelector />
            
            {/* Start Project Button */}
            <Link
              to="/start-project"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #374151 0%, #2563eb 100%)',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontSize: '0.9rem',
                textDecoration: 'none',
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #1f2937 0%, #1e40af 100%)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #374151 0%, #2563eb 100%)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.3)';
              }}
            >
              <Mail size={14} />
              <span>{t('nav.startProject')}</span>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            type="button"
            aria-expanded={isMobileMenuOpen}
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              display: isMobile ? 'flex' : 'none',
              background: 'rgba(248, 250, 252, 0.8)',
              border: '1px solid rgba(226, 232, 240, 0.6)',
              color: '#64748b',
              cursor: 'pointer',
              padding: '10px',
              borderRadius: '12px',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(241, 245, 249, 0.9)';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(248, 250, 252, 0.8)';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              key="mobile-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
              style={{
                position: 'fixed',
                inset: 0,
                top: `${NAVBAR_HEIGHT}px`,
                background: 'rgba(15, 23, 42, 0.35)',
                zIndex: 998,
              }}
            />
            <motion.div
              key="mobile-menu-panel"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              style={{
                position: 'fixed',
                top: `${NAVBAR_HEIGHT}px`,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                zIndex: 999,
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <div style={{
                padding: '20px',
                paddingBottom: '32px',
                borderBottom: '1px solid rgba(226, 232, 240, 0.3)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <img
                      src="/favicon.svg"
                      alt="DevAlpha"
                      width={32}
                      height={32}
                      style={{ borderRadius: '10px', display: 'block' }}
                    />
                    <span style={{
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #1e293b, #3b82f6)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      letterSpacing: '-0.5px'
                    }}>
                      DevAlpha
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label="Fermer le menu"
                    onClick={() => setIsMobileMenuOpen(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#64748b',
                      fontSize: '1.5rem',
                      cursor: 'pointer'
                    }}
                  >
                    <X size={24} />
                  </button>
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px'
                }}>
                  {/* Start Project Button - Mobile First */}
                  <Link
                    to="/start-project"
                    onClick={() => handleNavClick()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '15px 20px',
                      borderRadius: '12px',
                      background: 'rgba(59, 130, 246, 0.8)',
                      color: 'white',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      fontSize: '1rem',
                      textDecoration: 'none'
                    }}
                  >
                    <Mail size={18} />
                    <span>{t('nav.startProject')}</span>
                  </Link>

                  <div style={{
                    padding: '12px 0',
                    borderTop: '1px solid rgba(226, 232, 240, 0.5)',
                    borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
                    marginBottom: '8px'
                  }}>
                    <p style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '10px',
                      paddingLeft: '4px'
                    }}>
                      {t('nav.language')}
                    </p>
                    <LanguageSelector />
                  </div>

                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + index * 0.04, duration: 0.25, ease: 'easeOut' }}
                    >
                      <Link
                        to={link.path}
                        onClick={() => handleNavClick()}
                        style={{
                          textDecoration: 'none',
                          color: location.pathname === link.path ? '#3b82f6' : '#64748b',
                          fontWeight: location.pathname === link.path ? '600' : '500',
                          fontSize: '1.05rem',
                          padding: '13px 16px',
                          borderRadius: '12px',
                          background: location.pathname === link.path
                            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))'
                            : 'transparent',
                          border: location.pathname === link.path
                            ? '1px solid rgba(59, 130, 246, 0.2)'
                            : '1px solid transparent',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}
                      >
                        {location.pathname === link.path && (
                          <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#3b82f6',
                            flexShrink: 0
                          }} />
                        )}
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}

                  {/* Contact rapide */}
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + navLinks.length * 0.04, duration: 0.3 }}
                    style={{
                      marginTop: '8px',
                      padding: '16px',
                      borderRadius: '14px',
                      background: 'rgba(248, 250, 252, 0.8)',
                      border: '1px solid rgba(226, 232, 240, 0.6)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}
                  >
                    <a
                      href={`tel:${SITE_CONTACT.phone}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: '#475569',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        textDecoration: 'none'
                      }}
                    >
                      <Phone size={16} color="#3b82f6" />
                      {SITE_CONTACT.phoneDisplay}
                    </a>
                    <a
                      href={`mailto:${SITE_CONTACT.email}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: '#475569',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        textDecoration: 'none'
                      }}
                    >
                      <Mail size={16} color="#3b82f6" />
                      {SITE_CONTACT.email}
                    </a>
                    <a
                      href="https://t.me/devalpha_bot"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: '#475569',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        textDecoration: 'none'
                      }}
                    >
                      <MessageCircle size={16} color="#0088cc" />
                      @devalpha_bot
                    </a>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
